import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { ProfileService } from 'src/app/core/service/profile.service';
import { SiteMetadataService } from 'src/app/core/service/site-metadata.service';
import { IIdCard } from 'src/app/modules/sc-id-card-renderer/sc-id-card-renderer.component';

@Component({
  selector: 'app-id-card-viewer-modal',
  templateUrl: './id-card-viewer-modal.component.html',
  styleUrls: ['./id-card-viewer-modal.component.scss'],
})
export class IdCardViewerModalComponent implements OnInit {
  /* If a card number is passed, the modal renders that card directly. If not,
     it fetches the logged-in user's card; when none exists, the caller is
     expected to have triggered generation already (or we render a CTA). */
  @Input() cardNumber?: string;

  card: IIdCard | null = null;
  loading = true;

  constructor(
    private profileService: ProfileService,
    private siteMetadataService: SiteMetadataService,
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.siteMetadataService.setTitle('ID Card');
    if (this.cardNumber) {
      this.card = { number: this.cardNumber } as IIdCard;
      this.loading = false;
      return;
    }
    this.profileService.getMyIdCard().subscribe(
      (res) => {
        this.card = res?.data || null;
        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  /**
   * Resolve the actual `.id-card` element inside the iframe so the captured
   * PNG contains only the card — not the iframe's blank background that
   * surrounds it. The renderer is loaded as same-origin, so we can reach
   * into `contentDocument` without CORS issues. If anything is off (iframe
   * not loaded yet, content missing, etc.), we fall back to capturing the
   * iframe element itself rather than failing entirely.
   */
  private async captureCardElement(): Promise<HTMLElement | null> {
    const iframe = document.getElementById('id-card-div') as HTMLIFrameElement | null;
    if (!iframe) return null;
    const doc = iframe.contentDocument || iframe.contentWindow?.document || null;
    const card = doc?.querySelector('.id-card') as HTMLElement | null;
    return card || (iframe as unknown as HTMLElement);
  }

  /** PNG snapshot of just the rendered card (not the surrounding page). */
  async downloadAsImage(): Promise<void> {
    const el = await this.captureCardElement();
    if (!el) return;
    try {
      /* cacheBust avoids stale qrcode/img canvases the first time the iframe
         loads. backgroundColor white so any transparent corners on the card
         don't render as black on Safari. */
      const dataUrl = await toPng(el, { cacheBust: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = 'id-card.png';
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error('Failed to download as image', e);
    }
  }

  /** Single-page PDF derived from the rendered card.
   *
   *  Goal: visual parity with the PNG download — pure card, no margin,
   *  no surrounding whitespace. The PDF page is sized exactly to the
   *  card's aspect ratio so the image fills it edge-to-edge.
   *
   *  Trade-off: zero margin means the card kisses the page edge, and
   *  some printers clip 2-3mm. Since the typical use case is digital
   *  (sharing, archiving, attaching to email) rather than print, we
   *  optimise for the PNG-match look. Anyone who needs to print should
   *  use the printer's "fit to page" option (default in most drivers).
   */
  async downloadAsPdf(): Promise<void> {
    const el = await this.captureCardElement();
    if (!el) return;
    try {
      const dataUrl = await toPng(el, { cacheBust: true, backgroundColor: '#ffffff' });

      /* Throwaway PDF instance just to read the captured image's
         dimensions — jsPDF doesn't expose getImageProperties statically. */
      const probe = new jsPDF();
      const imgProps = probe.getImageProperties(dataUrl);

      /* Page size = card size (in mm). 200mm on the longer edge is a
         sensible physical scale: large enough to read on screen and
         still prints cleanly on standard paper if needed. The shorter
         edge derives from the card's pixel aspect ratio so the page is
         "card-shaped" and the image fills it 100%.

         IMPORTANT: jsPDF's `orientation` defaults to portrait. If you
         pass `format: [W, H]` with W > H but don't set orientation,
         jsPDF still renders a portrait page — the image then overflows
         the right edge and the bottom fills with whitespace. So we
         pick orientation explicitly from the card's actual shape. */
      const isLandscape = imgProps.width >= imgProps.height;
      const orientation: 'landscape' | 'portrait' = isLandscape ? 'landscape' : 'portrait';
      const longEdge = 200;                                        // mm
      const shortEdge = (Math.min(imgProps.width, imgProps.height)
                         / Math.max(imgProps.width, imgProps.height)) * longEdge;
      const pageWidth = isLandscape ? longEdge : shortEdge;
      const pageHeight = isLandscape ? shortEdge : longEdge;

      const pdf = new jsPDF({
        unit: 'mm',
        orientation,
        format: [pageWidth, pageHeight],
      });

      /* Position (0, 0) and full page dimensions = zero margin,
         no surrounding whitespace. Matches the PNG output exactly. */
      pdf.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
      pdf.save('id-card.pdf');
    } catch (e) {
      console.error('Failed to download as PDF', e);
    }
  }

  closeModal(): void {
    this.activeModal.close();
  }
}
